import React, { useState, useEffect } from 'react';
import {
  TouchableOpacity,
  Text,
  View,
  StyleSheet,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { hybridManager, syncQueue } from '../../services/offline';

interface ManualSyncButtonProps {
  // Styling props
  style?: any;
  variant?: 'primary' | 'secondary' | 'icon-only' | 'fab'; // floating action button
  size?: 'small' | 'medium' | 'large';
  
  // Behavior props  
  showLabel?: boolean;
  showStatus?: boolean;
  disabled?: boolean;
  
  // Callback props
  onSyncStart?: () => void;
  onSyncComplete?: (success: boolean) => void;
  onSyncError?: (error: Error) => void;
}

// Define colors since we don't have theme
const colors = {
  primary: '#007AFF',
  success: '#4CAF50',
  warning: '#FF9800',
  error: '#f44336',
  disabled: '#CCCCCC',
};

export const ManualSyncButton: React.FC<ManualSyncButtonProps> = ({
  style,
  variant = 'primary',
  size = 'medium',
  showLabel = true,
  showStatus = true,
  disabled = false,
  onSyncStart,
  onSyncComplete,
  onSyncError,
}) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [pendingItems, setPendingItems] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize hybrid system and check status
  useEffect(() => {
    const initializeAndCheckStatus = async () => {
      try {
        console.log('🔄 Initializing hybrid system for manual sync...');
        
        // Initialize hybrid manager if not already initialized
        await hybridManager.initialize();
        setIsInitialized(true);
        console.log('✅ Hybrid system initialized for manual sync');
        
        // Basic connection check
        setIsOnline(true); // Assume online for now
        
        // Check for pending sync items
        try {
          const status = await hybridManager.getSystemStatus();
          setPendingItems(status.storage.pendingSync || 0);
          setIsOnline(status.isOnline);
        } catch (statusError) {
          console.log('Status check failed, using defaults:', statusError);
          setPendingItems(0);
        }
      } catch (error) {
        console.error('Hybrid system initialization failed:', error);
        setIsInitialized(false);
      }
    };
    
    initializeAndCheckStatus();
    
    // Check status every 30 seconds
    const interval = setInterval(async () => {
      if (isInitialized) {
        try {
          const status = await hybridManager.getSystemStatus();
          setPendingItems(status.storage.pendingSync || 0);
          setIsOnline(status.isOnline);
        } catch (error) {
          console.log('Status update failed:', error);
        }
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const needsSync = pendingItems > 0;
  const canSync = !disabled && !isSyncing;

  const handleManualSync = async () => {
    if (!canSync) {
      return;
    }

    setIsSyncing(true);
    onSyncStart?.();

    try {
      console.log('🔄 Manual sync initiated by user');
      
      // Try multiple approaches to ensure sync works
      let syncSuccessful = false;
      
      // Approach 1: Try direct syncQueue (bypasses HybridManager state check)
      try {
        console.log('🔄 Attempting direct sync via SyncQueue...');
        await syncQueue.processPendingItems();
        syncSuccessful = true;
        console.log('✅ Direct sync completed successfully');
      } catch (directSyncError) {
        console.log('⚠️ Direct sync failed, trying HybridManager approach...');
        
        // Approach 2: Initialize HybridManager and try forceSync
        try {
          console.log('🔄 Initializing HybridManager...');
          await hybridManager.initialize();
          setIsInitialized(true);
          
          console.log('🔄 Attempting HybridManager.forceSync...');
          await hybridManager.forceSync();
          syncSuccessful = true;
          console.log('✅ HybridManager sync completed successfully');
        } catch (hybridSyncError) {
          console.log('⚠️ HybridManager sync also failed:', hybridSyncError);
          // If both fail, throw the original error
          throw directSyncError;
        }
      }
      
      if (!syncSuccessful) {
        throw new Error('All sync methods failed');
      }
      
      console.log('✅ Manual sync completed successfully');
      onSyncComplete?.(true);
      
      // Refresh status after sync
      try {
        // Try to get status, but don't fail if it doesn't work
        if (isInitialized) {
          const status = await hybridManager.getSystemStatus();
          setPendingItems(status.storage.pendingSync || 0);
        } else {
          // Just reset to 0 if we can't get status
          setPendingItems(0);
        }
      } catch (statusError) {
        console.log('Status refresh failed, assuming sync completed:', statusError);
        setPendingItems(0);
      }
      
      // Show success feedback
      Alert.alert(
        'Sync Complete',
        'Data synchronization completed successfully!',
        [{ text: 'OK' }]
      );

    } catch (error) {
      console.error('❌ Manual sync failed:', error);
      onSyncError?.(error as Error);
      onSyncComplete?.(false);
      
      const errorMessage = (error as Error).message;
      
      // Handle specific error cases with user-friendly messages
      if (errorMessage.includes('not initialized') || errorMessage.includes('System failed')) {
        Alert.alert(
          'Sync Temporarily Unavailable',
          'The sync system is starting up. Please wait a moment and try again.',
          [{ text: 'OK' }]
        );
        setIsInitialized(false);
      } else if (errorMessage.includes('offline') || errorMessage.includes('CONNECTION_REFUSED') || errorMessage.includes('network')) {
        Alert.alert(
          'Connection Problem',
          'Unable to reach the server. Please check your internet connection and try again.',
          [{ text: 'OK' }]
        );
        setIsOnline(false);
      } else if (errorMessage.includes('pending') || errorMessage.includes('queue')) {
        Alert.alert(
          'No Data to Sync',
          'All your data is already synchronized with the server.',
          [{ text: 'OK' }]
        );
      } else {
        // Generic error with helpful message
        Alert.alert(
          'Sync Issue',
          'There was a problem synchronizing your data. Your data is safe and the sync will retry automatically later.',
          [{ text: 'OK' }]
        );
      }
    } finally {
      setIsSyncing(false);
    }
  };

  const getSyncStatusText = () => {
    if (!isInitialized) return 'Initializing...';
    if (isSyncing) return 'Syncing...';
    if (!isOnline) return 'Offline';
    if (pendingItems > 0) return `${pendingItems} pending`;
    return 'Up to date';
  };

  const getSyncStatusColor = () => {
    if (!isInitialized) return colors.warning;
    if (isSyncing) return colors.primary;
    if (!isOnline) return colors.error;
    if (pendingItems > 0) return colors.warning;
    return colors.success;
  };

  // Determine button styles based on variant and size
  const getButtonStyles = () => {
    let buttonStyle = { ...styles.button };
    
    // Size styles
    switch (size) {
      case 'small':
        buttonStyle = { ...buttonStyle, ...styles.buttonSmall };
        break;
      case 'large':
        buttonStyle = { ...buttonStyle, ...styles.buttonLarge };
        break;
      default:
        buttonStyle = { ...buttonStyle, ...styles.buttonMedium };
    }

    // Variant styles
    switch (variant) {
      case 'secondary':
        buttonStyle = { ...buttonStyle, ...styles.buttonSecondary };
        break;
      case 'icon-only':
        buttonStyle = { ...buttonStyle, ...styles.buttonIconOnly };
        break;
      case 'fab':
        buttonStyle = { ...buttonStyle, ...styles.buttonFab };
        break;
      default:
        buttonStyle = { ...buttonStyle, ...styles.buttonPrimary };
    }

    // State styles
    if (!canSync) {
      buttonStyle = { ...buttonStyle, ...styles.buttonDisabled };
    }

    return [buttonStyle, style];
  };

  const getTextStyles = () => {
    if (variant === 'icon-only' || variant === 'fab') {
      return null; // No text for icon-only variants
    }

    let textStyle = { ...styles.buttonText };
    
    switch (variant) {
      case 'secondary':
        textStyle = { ...textStyle, ...styles.buttonTextSecondary };
        break;
      default:
        textStyle = { ...textStyle, ...styles.buttonTextPrimary };
    }
    
    if (!canSync) {
      textStyle = { ...textStyle, ...styles.buttonTextDisabled };
    }
    
    return textStyle;
  };

  const getIconSize = () => {
    switch (size) {
      case 'small': return 16;
      case 'large': return 28;
      default: return 20;
    }
  };

  const getIconColor = () => {
    if (variant === 'secondary') {
      return canSync ? colors.primary : colors.disabled;
    }
    return canSync ? '#FFFFFF' : colors.disabled;
  };

  const renderIcon = () => {
    if (!isInitialized || isSyncing) {
      return (
        <ActivityIndicator 
          size={size === 'small' ? 'small' : 'small'} 
          color={getIconColor()} 
        />
      );
    }
    
    if (!isOnline) {
      return (
        <Ionicons 
          name="cloud-offline" 
          size={getIconSize()} 
          color={getIconColor()} 
        />
      );
    }
    
    return (
      <Ionicons 
        name={needsSync ? 'cloud-upload' : 'checkmark-circle'} 
        size={getIconSize()} 
        color={getIconColor()} 
      />
    );
  };

  const textStyles = getTextStyles();

  return (
    <View style={[styles.container, style]}>
      <TouchableOpacity
        style={getButtonStyles()}
        onPress={handleManualSync}
        disabled={!canSync}
        activeOpacity={0.7}
      >
        {renderIcon()}
        
        {showLabel && textStyles && (
          <Text style={textStyles}>
            {!isInitialized ? 'Loading...' : isSyncing ? 'Syncing...' : 'Sync'}
          </Text>
        )}
      </TouchableOpacity>
      
      {showStatus && variant !== 'fab' && (
        <Text style={[styles.statusText, { color: getSyncStatusColor() }]}>
          {getSyncStatusText()}
        </Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
  },
  
  // Base button styles
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  
  // Size variants
  buttonSmall: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    gap: 4,
  },
  buttonMedium: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    gap: 6,
  },
  buttonLarge: {
    paddingHorizontal: 20,
    paddingVertical: 14,
    gap: 8,
  },
  
  // Style variants
  buttonPrimary: {
    backgroundColor: colors.primary,
  },
  buttonSecondary: {
    backgroundColor: 'transparent',
    borderWidth: 1.5,
    borderColor: colors.primary,
    elevation: 0,
    shadowOpacity: 0,
  },
  buttonIconOnly: {
    paddingHorizontal: 12,
    paddingVertical: 12,
    backgroundColor: colors.primary,
  },
  buttonFab: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: colors.primary,
    position: 'absolute',
    bottom: 20,
    right: 20,
    elevation: 6,
    shadowOpacity: 0.3,
  },
  
  // State variants
  buttonDisabled: {
    backgroundColor: colors.disabled,
    borderColor: colors.disabled,
    elevation: 0,
    shadowOpacity: 0,
  },
  
  // Text styles
  buttonText: {
    fontWeight: '600',
    fontSize: 14,
  },
  buttonTextPrimary: {
    color: '#FFFFFF',
  },
  buttonTextSecondary: {
    color: colors.primary,
  },
  buttonTextDisabled: {
    color: colors.disabled,
  },
  
  // Status text
  statusText: {
    fontSize: 12,
    marginTop: 4,
    fontWeight: '500',
  },
});

export default ManualSyncButton;